"use client";

import * as React from "react";
import { createPortal } from "react-dom";

import { focusableElements } from "./focus-context";

import { cn } from "~/lib/utils";

const modalStack: HTMLElement[] = [];
let originalBodyOverflow = "";

/**
 * P0-11 · Modal dialog. Portals to <body>, traps Tab focus, closes on Esc /
 * overlay click, locks scroll, and restores focus to the opener on close.
 */
export function Dialog({
  open,
  onOpenChange,
  children,
  className,
  panelStyle,
  ariaLabel,
  placement = "center",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  /**
   * P9-14 · Inline style on the panel, for values that can't be a class —
   * specifically the live `translateY` of a drag-to-dismiss bottom sheet.
   */
  panelStyle?: React.CSSProperties;
  ariaLabel?: string;
  /**
   * "center" is the modal default; "right" anchors the panel as a full-height
   * side drawer (P1-11 asset detail), collapsing to a bottom sheet on phones.
   * "left" is a full-height drawer at every size (P2-01 mobile nav — phones
   * are its primary surface, so it never collapses to a sheet).
   * "fullscreen" covers the visible viewport edge to edge, no frame (the PDP
   * image viewer, which needs every pixel of a phone screen for zooming).
   */
  placement?: "center" | "right" | "left" | "fullscreen";
}) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const dialogId = React.useId();
  const dismissRef = React.useRef(onOpenChange);
  dismissRef.current = onOpenChange;
  const openerRef = React.useRef<HTMLElement | null>(null);
  const previousOpen = React.useRef(false);
  // Capture before portal children (including autoFocus inputs) are committed.
  if (open && !previousOpen.current && typeof document !== "undefined") {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  previousOpen.current = open;
  const [mounted, setMounted] = React.useState(false);
  const [viewport, setViewport] = React.useState<{ height: number; width: number; top: number; left: number } | null>(null);
  React.useEffect(() => setMounted(true), []);

  React.useEffect(() => {
    if (!mounted || !open || (placement !== "center" && placement !== "fullscreen")) return;
    const update = () => {
      const visible = window.visualViewport;
      setViewport(visible ? { height: visible.height, width: visible.width, top: visible.offsetTop, left: visible.offsetLeft } : null);
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && panelRef.current?.contains(focused)) {
        focused.scrollIntoView?.({ block: "nearest" });
      }
    };
    update();
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [mounted, open, placement]);

  React.useEffect(() => {
    const panel = panelRef.current;
    if (!mounted || !open || !panel) return;
    if (modalStack.length === 0) {
      originalBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    modalStack.push(panel);
    const focusables = () => {
      const notifications = Array.from(document.querySelectorAll<HTMLElement>("[data-toast-dialog]"))
        .filter((toast) => toast.dataset.toastDialog === dialogId);
      return [...new Set([...focusableElements(panel), ...notifications.flatMap(focusableElements)])];
    };
    const updateOwnedNotifications = () => {
      const ids = Array.from(document.querySelectorAll<HTMLElement>("[data-toast-dialog]"))
        .filter((toast) => toast.dataset.toastDialog === dialogId && !panel.contains(toast)).map((toast) => toast.id).filter(Boolean);
      if (ids.length) panel.setAttribute("aria-owns", ids.join(" "));
      else panel.removeAttribute("aria-owns");
    };
    updateOwnedNotifications();
    const observer = new MutationObserver(updateOwnedNotifications);
    observer.observe(document.body, { childList: true, subtree: true });
    // Honor an explicit autoFocus child; otherwise choose the first available control.
    if (!panel.contains(document.activeElement)) (focusables()[0] ?? panel).focus();

    function onKey(e: KeyboardEvent) {
      if (modalStack.at(-1) !== panel || e.defaultPrevented) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        dismissRef.current(false);
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      const first = items[0];
      const last = items.at(-1);
      if (!first || !last) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const current = document.activeElement;
      const index = items.findIndex((item) => item === current);
      // One deduplicated DOM order covers both inline notices and any
      // externally owned controls during a portal transition.
      e.preventDefault();
      const next = index < 0 ? (e.shiftKey ? last : first) : items[(index + (e.shiftKey ? -1 : 1) + items.length) % items.length];
      next?.focus();
    }

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      observer.disconnect();
      const wasTop = modalStack.at(-1) === panel;
      const index = modalStack.indexOf(panel);
      if (index >= 0) modalStack.splice(index, 1);
      if (modalStack.length === 0) document.body.style.overflow = originalBodyOverflow;
      if (wasTop) {
        const remaining = modalStack.at(-1);
        const opener = openerRef.current;
        if (opener?.isConnected && (!remaining || remaining.contains(opener))) opener.focus();
        else if (remaining) (focusableElements(remaining)[0] ?? remaining).focus();
      }
    };
  }, [mounted, open, dialogId]);

  if (!mounted || !open) return null;

  return createPortal(
    <div
      style={(placement === "center" || placement === "fullscreen") && viewport ? {
        top: viewport.top, left: viewport.left, bottom: "auto", right: "auto",
        height: viewport.height, width: viewport.width,
        "--dialog-viewport-height": `${viewport.height}px`,
      } as React.CSSProperties : undefined}
      className={cn(
        "fixed inset-0 z-50 flex",
        placement === "center" && "items-center justify-center p-4",
        placement === "right" &&
          "items-end justify-center sm:items-stretch sm:justify-end",
        placement === "left" && "items-stretch justify-start",
        placement === "fullscreen" && "items-stretch justify-stretch",
      )}
    >
      <div
        className="absolute inset-0 bg-black/50"
        aria-hidden
        onClick={() => onOpenChange(false)}
      />
      <div
        ref={panelRef}
        data-dialog-id={dialogId}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        style={panelStyle}
        className={cn(
          "relative z-10 w-full bg-surface text-fg focus:outline-none",
          // `cn` does not merge, so the frame is left off rather than overridden.
          placement !== "fullscreen" && "border border-border p-5 shadow-[var(--shadow-lg)]",
          placement === "center" && "max-h-[calc(var(--dialog-viewport-height,100dvh)-2rem)] max-w-md overflow-y-auto overscroll-contain rounded-lg",
          placement === "right" &&
            "max-h-[85vh] overflow-y-auto rounded-t-lg sm:max-h-none sm:max-w-sm sm:rounded-none sm:border-y-0 sm:border-r-0",
          placement === "left" &&
            "max-w-[20rem] overflow-y-auto rounded-none border-y-0 border-l-0",
          placement === "fullscreen" &&
            "h-full overflow-hidden",
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function DialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div className={cn("mb-3 flex flex-col gap-1", className)} {...props} />
  );
}
export function DialogTitle({
  className,
  ...props
}: React.ComponentProps<"h2">) {
  return (
    <h2
      className={cn("font-heading text-lg font-semibold text-fg", className)}
      {...props}
    />
  );
}
export function DialogDescription({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return <p className={cn("text-sm text-fg-muted", className)} {...props} />;
}
export function DialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div className={cn("mt-5 flex flex-wrap justify-end gap-2 [&_button]:h-auto [&_button]:min-h-10 [&_button]:max-w-full [&_button]:whitespace-normal", className)} {...props} />
  );
}
